<template>
  <div class="emp-documents-page">
    <!-- 앱 헤더 -->
    <div class="app-header">
      <div class="header-title">문서함</div>
    </div>

    <div class="page-content">

    <!-- 문서 카테고리 -->
    <div class="document-categories">
      <!-- 근로계약서 -->
      <div class="category-card" @click="openCategory('contract')">
        <div class="card-icon contract">
          <v-icon size="28" color="white">mdi-file-sign</v-icon>
        </div>
        <div class="card-content">
          <h3>근로계약서</h3>
          <p>나의 근로계약서 확인</p>
        </div>
        <div class="card-badge" v-if="documentCounts.contract > 0">
          {{ documentCounts.contract }}
        </div>
        <v-icon size="20" color="#9ca3af">mdi-chevron-right</v-icon>
      </div>

      <!-- 급여명세서 -->
      <div class="category-card" @click="openCategory('payslip')">
        <div class="card-icon payslip">
          <v-icon size="28" color="white">mdi-currency-krw</v-icon>
        </div>
        <div class="card-content">
          <h3>급여명세서</h3>
          <p>월별 급여명세서 다운로드</p>
        </div>
        <div class="card-badge" v-if="documentCounts.payslip > 0">
          {{ documentCounts.payslip }}
        </div>
        <v-icon size="20" color="#9ca3af">mdi-chevron-right</v-icon>
      </div>

      <!-- 서비스 기록지 (요양보호사만) -->
      <div class="category-card" v-if="isCaregiver" @click="openCategory('record')">
        <div class="card-icon record">
          <v-icon size="28" color="white">mdi-clipboard-text</v-icon>
        </div>
        <div class="card-content">
          <h3>서비스 기록지</h3>
          <p>방문일지 및 서비스 기록</p>
        </div>
        <div class="card-badge" v-if="documentCounts.record > 0">
          {{ documentCounts.record }}
        </div>
        <v-icon size="20" color="#9ca3af">mdi-chevron-right</v-icon>
      </div>

      <!-- 증명서 발급 -->
      <div class="category-card" @click="openCategory('certificate')">
        <div class="card-icon certificate">
          <v-icon size="28" color="white">mdi-certificate</v-icon>
        </div>
        <div class="card-content">
          <h3>증명서 발급</h3>
          <p>재직증명서, 경력증명서 요청</p>
        </div>
        <div class="card-arrow">
          <v-icon size="20" color="#9ca3af">mdi-chevron-right</v-icon>
        </div>
      </div>

      <!-- 사직서 -->
      <div class="category-card resignation" @click="openResignation">
        <div class="card-icon resignation">
          <v-icon size="28" color="white">mdi-file-remove</v-icon>
        </div>
        <div class="card-content">
          <h3>사직서</h3>
          <p>사직서 작성 및 제출</p>
        </div>
        <div class="card-status" v-if="myResignation">
          <span :class="getResignationStatusClass(myResignation.status)">
            {{ getResignationStatusText(myResignation.status) }}
          </span>
        </div>
        <v-icon size="20" color="#9ca3af">mdi-chevron-right</v-icon>
      </div>
    </div>

    <!-- 최근 문서 -->
    <div class="recent-documents" v-if="recentDocuments.length > 0">
      <h2 class="section-title">
        <v-icon size="20" color="#059669">mdi-clock-outline</v-icon>
        최근 문서
      </h2>
      <div class="document-list">
        <div 
          v-for="doc in recentDocuments" 
          :key="doc.id"
          class="document-item"
          @click="openDocument(doc)"
        >
          <div class="doc-icon" :class="doc.type">
            <v-icon size="20" color="white">{{ getDocIcon(doc.type) }}</v-icon>
          </div>
          <div class="doc-info">
            <span class="doc-name">{{ doc.name }}</span>
            <span class="doc-date">{{ formatDate(doc.date) }}</span>
          </div>
          <button class="download-btn" @click.stop="downloadDocument(doc)">
            <v-icon size="18" color="#059669">mdi-download</v-icon>
          </button>
        </div>
      </div>
    </div>

    <!-- 문서 없음 안내 -->
    <div class="empty-notice" v-if="recentDocuments.length === 0 && !isLoading">
      <v-icon size="48" color="#d1d5db">mdi-folder-open-outline</v-icon>
      <p>최근 문서가 없습니다</p>
      <span>카테고리를 선택하여 문서를 확인하세요</span>
    </div>
    </div>

    <!-- 근로계약서 상세 다이얼로그 -->
    <v-dialog v-model="showContractDialog" max-width="450" >
      <v-card class="document-dialog-card">
        <div class="dialog-header">
          <h3>근로계약서</h3>
          <button class="close-btn" @click="showContractDialog = false">
            <v-icon>mdi-close</v-icon>
          </button>
        </div>
        <div class="dialog-content">
          <div v-if="contracts.length > 0">
            <div 
              v-for="contract in contracts" 
              :key="contract.id"
              class="contract-item"
            >
              <div class="contract-info">
                <span class="contract-period">{{ contract.period }}</span>
                <span class="contract-type">{{ contract.type }}</span>
              </div>
              <div class="contract-actions">
                <button class="view-btn" @click="viewContract(contract)">
                  <v-icon size="18">mdi-eye</v-icon>
                </button>
                <button class="download-btn" @click="downloadContract(contract)">
                  <v-icon size="18">mdi-download</v-icon>
                </button>
              </div>
            </div>
          </div>
          <div v-else class="no-data">
            <v-icon size="40" color="#d1d5db">mdi-file-document-outline</v-icon>
            <p>등록된 근로계약서가 없습니다</p>
          </div>
        </div>
      </v-card>
    </v-dialog>

    <!-- 증명서 발급 요청 다이얼로그 -->
    <v-dialog v-model="showCertificateDialog" max-width="400" >
      <v-card class="document-dialog-card">
        <div class="dialog-header">
          <h3>증명서 발급 요청</h3>
          <button class="close-btn" @click="showCertificateDialog = false">
            <v-icon>mdi-close</v-icon>
          </button>
        </div>
        <div class="dialog-content">
          <div class="certificate-options">
            <button 
              class="cert-option"
              :class="{ active: selectedCertType === 'employment' }"
              @click="selectedCertType = 'employment'"
            >
              <v-icon size="24" :color="selectedCertType === 'employment' ? '#059669' : '#9ca3af'">
                mdi-badge-account-horizontal
              </v-icon>
              <span>재직증명서</span>
            </button>
            <button 
              class="cert-option"
              :class="{ active: selectedCertType === 'career' }"
              @click="selectedCertType = 'career'"
            >
              <v-icon size="24" :color="selectedCertType === 'career' ? '#059669' : '#9ca3af'">
                mdi-certificate
              </v-icon>
              <span>경력증명서</span>
            </button>
          </div>
          <div class="cert-purpose">
            <label>발급 용도</label>
            <input 
              type="text" 
              v-model="certPurpose" 
              placeholder="예: 은행 제출용, 관공서 제출용"
            />
          </div>
          <button 
            class="request-btn"
            :disabled="!selectedCertType || isRequesting"
            @click="requestCertificate"
          >
            <v-progress-circular v-if="isRequesting" indeterminate size="20" color="white" />
            <span v-else>발급 요청</span>
          </button>
          <p class="cert-notice">
            <v-icon size="14" color="#d97706">mdi-information-outline</v-icon>
            요청 후 관리자 승인 시 발급됩니다
          </p>
        </div>
      </v-card>
    </v-dialog>

    <!-- 사직서 작성 다이얼로그 -->
    <v-dialog v-model="showResignationWriteDialog" max-width="420" >
      <v-card class="resignation-write-card">
        <div class="write-header">
          <v-icon size="28" color="#dc2626">mdi-file-document-edit</v-icon>
          <h2>사직서 작성</h2>
          <button class="close-btn" @click="closeResignationDialog">
            <v-icon size="22">mdi-close</v-icon>
          </button>
        </div>
        
        <div class="write-content">
          <!-- 퇴사 희망일 -->
          <div class="input-group">
            <label>
              <v-icon size="18" color="#6b7280">mdi-calendar</v-icon>
              퇴사 희망일
            </label>
            <input 
              type="date" 
              v-model="resignationData.resignDate" 
              class="date-input"
              :min="minResignDate"
            />
            <p class="input-hint">최소 2주 후 날짜부터 선택 가능합니다</p>
          </div>

          <!-- 사직 사유 -->
          <div class="input-group">
            <label>
              <v-icon size="18" color="#6b7280">mdi-text-box</v-icon>
              사직 사유
            </label>
            <textarea 
              v-model="resignationData.reason"
              class="reason-input"
              placeholder="사직 사유를 입력하세요"
              rows="4"
            ></textarea>
          </div>

          <!-- 서명 -->
          <div class="input-group">
            <label>
              <v-icon size="18" color="#6b7280">mdi-draw</v-icon>
              서명
            </label>
            <div class="signature-box">
              <canvas 
                ref="signatureCanvas"
                class="signature-canvas"
                @mousedown="startDrawing"
                @mousemove="draw"
                @mouseup="stopDrawing"
                @mouseleave="stopDrawing"
                @touchstart="startDrawingTouch"
                @touchmove="drawTouch"
                @touchend="stopDrawing"
              ></canvas>
              <button class="clear-btn" @click="clearSignature" type="button">
                <v-icon size="16">mdi-refresh</v-icon>
                다시 그리기
              </button>
            </div>
          </div>

          <!-- 안내 -->
          <div class="info-notice">
            <v-icon size="16" color="#0369a1">mdi-information</v-icon>
            <span>제출 시 자동으로 사직서 PDF가 생성됩니다</span>
          </div>

          <!-- 경고 -->
          <div class="warning-notice">
            <v-icon size="16" color="#dc2626">mdi-alert-circle</v-icon>
            <span>제출 후 삭제는 승인 전까지만 가능합니다</span>
          </div>

          <!-- 제출 버튼 -->
          <button 
            class="submit-btn"
            :disabled="!canSubmitResignation || isSubmitting"
            @click="submitResignation"
          >
            <v-progress-circular v-if="isSubmitting" indeterminate size="20" color="white" />
            <template v-else>
              <v-icon size="20">mdi-send</v-icon>
              <span>사직서 제출</span>
            </template>
          </button>
        </div>
      </v-card>
    </v-dialog>

    <!-- 제출된 사직서 상세 다이얼로그 -->
    <v-dialog v-model="showResignationDetailDialog" max-width="500" >
      <v-card class="resignation-detail-card">
        <div class="resignation-detail-header">
          <h2>제출된 사직서</h2>
          <button class="close-btn" @click="showResignationDetailDialog = false">
            <v-icon color="white">mdi-close</v-icon>
          </button>
        </div>
        
        <div class="resignation-detail-content" v-if="myResignation">
          <!-- 상태 배지 -->
          <div class="status-badge" :class="myResignation.status">
            {{ getResignationStatusText(myResignation.status) }}
          </div>

          <!-- 사직서 정보 -->
          <div class="detail-info">
            <div class="info-row">
              <span class="info-label">소속</span>
              <span class="info-value">{{ myResignation.content?.sisul_name || sisulName }}</span>
            </div>
            <div class="info-row">
              <span class="info-label">직위</span>
              <span class="info-value">{{ myResignation.content?.emp_job || empJob }}</span>
            </div>
            <div class="info-row">
              <span class="info-label">성명</span>
              <span class="info-value">{{ myResignation.content?.emp_name || empName }}</span>
            </div>
            <div class="info-row">
              <span class="info-label">사직사유</span>
              <span class="info-value reason">{{ myResignation.content?.resign_reason }}</span>
            </div>
            <div class="info-row">
              <span class="info-label">퇴사 희망일</span>
              <span class="info-value">{{ formatDate(myResignation.resign_date) }}</span>
            </div>
            <div class="info-row">
              <span class="info-label">제출일</span>
              <span class="info-value">{{ formatDate(myResignation.submit_date) }}</span>
            </div>
          </div>

          <!-- PDF 다운로드 -->
          <button class="download-pdf-btn" @click="downloadResignationPDF" v-if="myResignation.pdf_path">
            <v-icon size="20">mdi-file-pdf-box</v-icon>
            <span>사직서 PDF 다운로드</span>
          </button>

          <!-- 삭제 버튼 (승인 전에만) -->
          <button 
            class="delete-btn" 
            @click="confirmDeleteResignation"
            v-if="myResignation.status === 'submitted'"
          >
            <v-icon size="20">mdi-delete</v-icon>
            <span>사직서 삭제</span>
          </button>

          <p class="delete-notice" v-if="myResignation.status !== 'submitted'">
            <v-icon size="14" color="#9ca3af">mdi-information-outline</v-icon>
            이미 처리된 사직서는 삭제할 수 없습니다.
          </p>
        </div>
      </v-card>
    </v-dialog>

    <!-- 삭제 확인 다이얼로그 -->
    <v-dialog v-model="showDeleteConfirmDialog" max-width="340">
      <v-card class="confirm-dialog-card">
        <div class="confirm-content">
          <v-icon size="48" color="#dc2626">mdi-alert-circle-outline</v-icon>
          <h3>사직서 삭제</h3>
          <p>제출한 사직서를 삭제하시겠습니까?<br/>삭제 후에는 복구할 수 없습니다.</p>
        </div>
        <div class="confirm-actions">
          <button class="cancel-btn" @click="showDeleteConfirmDialog = false">취소</button>
          <button class="delete-confirm-btn" @click="deleteResignation" :disabled="isDeleting">
            <v-progress-circular v-if="isDeleting" indeterminate size="18" color="white" />
            <span v-else>삭제</span>
          </button>
        </div>
      </v-card>
    </v-dialog>

    <!-- 알림 스낵바 -->
    <v-snackbar v-model="showSnackbar" :color="snackbarColor" :timeout="3000">
      {{ snackbarMessage }}
    </v-snackbar>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, nextTick, watch } from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import axios from 'axios';

const store = useStore();
const router = useRouter();

// 사원 정보
const pwaUser = computed(() => store.getters['pwa/pwaUser']);
const empIdx = computed(() => pwaUser.value?.emp_idx || '');
const empCode = computed(() => pwaUser.value?.emp_code || '');
const sisulCode = computed(() => pwaUser.value?.m_sisul_code || '');
const sisulName = computed(() => pwaUser.value?.sisul_name || pwaUser.value?.m_sisul_name || '');
const empJob = computed(() => pwaUser.value?.emp_job || '');
const empName = computed(() => pwaUser.value?.emp_name || '');

// 요양보호사 여부
const isCaregiver = computed(() => {
  const job = empJob.value;
  return !job.includes('센터장') && !job.includes('사회복지사') && !job.includes('사무');
});

// 상태
const isLoading = ref(false);
const isRequesting = ref(false);
const isSubmitting = ref(false);
const isDeleting = ref(false);

// 문서 카운트
const documentCounts = ref({
  contract: 0,
  payslip: 0,
  record: 0
});

// 최근 문서
const recentDocuments = ref<any[]>([]);

// 근로계약서
const showContractDialog = ref(false);
const contracts = ref<any[]>([]);

// 증명서 발급
const showCertificateDialog = ref(false);
const selectedCertType = ref('');
const certPurpose = ref('');

// 사직서
const showResignationWriteDialog = ref(false);
const showResignationDetailDialog = ref(false);
const showDeleteConfirmDialog = ref(false);
const myResignation = ref<any>(null);
const resignationData = ref({
  reason: '',
  resignDate: ''
});

// 오늘 날짜
const today = new Date();
const todayFormatted = `${today.getFullYear()}년 ${today.getMonth() + 1}월 ${today.getDate()}일`;
const todayISO = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;

// 최소 퇴사 희망일 (오늘 + 14일)
const minResignDate = computed(() => {
  const min = new Date();
  min.setDate(min.getDate() + 14);
  return min.toISOString().split('T')[0];
});

// 서명 캔버스
const signatureCanvas = ref<HTMLCanvasElement | null>(null);
let ctx: CanvasRenderingContext2D | null = null;
let isDrawing = false;
let hasSignature = ref(false);

// 사직서 제출 가능 여부
const canSubmitResignation = computed(() => {
  return resignationData.value.reason.trim() !== '' && 
         resignationData.value.resignDate !== '' &&
         hasSignature.value;
});

// 서명 캔버스 초기화
const initSignatureCanvas = () => {
  nextTick(() => {
    if (signatureCanvas.value) {
      const canvas = signatureCanvas.value;
      canvas.width = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
      ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.strokeStyle = '#000000';
        ctx.lineWidth = 2;
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';
      }
    }
  });
};

// 마우스 이벤트
const startDrawing = (e: MouseEvent) => {
  if (!ctx) return;
  isDrawing = true;
  const rect = signatureCanvas.value!.getBoundingClientRect();
  ctx.beginPath();
  ctx.moveTo(e.clientX - rect.left, e.clientY - rect.top);
};

const draw = (e: MouseEvent) => {
  if (!isDrawing || !ctx) return;
  const rect = signatureCanvas.value!.getBoundingClientRect();
  ctx.lineTo(e.clientX - rect.left, e.clientY - rect.top);
  ctx.stroke();
  hasSignature.value = true;
};

const stopDrawing = () => {
  isDrawing = false;
};

// 터치 이벤트
const startDrawingTouch = (e: TouchEvent) => {
  e.preventDefault();
  if (!ctx || !signatureCanvas.value) return;
  isDrawing = true;
  const rect = signatureCanvas.value.getBoundingClientRect();
  const touch = e.touches[0];
  ctx.beginPath();
  ctx.moveTo(touch.clientX - rect.left, touch.clientY - rect.top);
};

const drawTouch = (e: TouchEvent) => {
  e.preventDefault();
  if (!isDrawing || !ctx || !signatureCanvas.value) return;
  const rect = signatureCanvas.value.getBoundingClientRect();
  const touch = e.touches[0];
  ctx.lineTo(touch.clientX - rect.left, touch.clientY - rect.top);
  ctx.stroke();
  hasSignature.value = true;
};

// 서명 지우기
const clearSignature = () => {
  if (ctx && signatureCanvas.value) {
    ctx.clearRect(0, 0, signatureCanvas.value.width, signatureCanvas.value.height);
    hasSignature.value = false;
  }
};

// 사직서 다이얼로그 닫기
const closeResignationDialog = () => {
  showResignationWriteDialog.value = false;
  resignationData.value = { reason: '', resignDate: '' };
  clearSignature();
};

// 사직서 다이얼로그 watch
watch(showResignationWriteDialog, (val) => {
  if (val) {
    initSignatureCanvas();
  }
});

// 스낵바
const showSnackbar = ref(false);
const snackbarMessage = ref('');
const snackbarColor = ref('success');

// 사직서 열기
const openResignation = async () => {
  await fetchMyResignation();
  
  if (myResignation.value) {
    // 이미 제출된 사직서가 있음 -> 상세 보기
    showResignationDetailDialog.value = true;
  } else {
    // 사직서 없음 -> 작성 화면
    showResignationWriteDialog.value = true;
    resignationData.value = { reason: '', resignDate: minResignDate.value };
  }
};

// 내 사직서 조회
const fetchMyResignation = async () => {
  try {
    const response = await axios.get(`/api/emp/resignation/${empIdx.value}`, {
      withCredentials: true
    });
    
    if (response.data.success && response.data.data) {
      myResignation.value = response.data.data;
    } else {
      myResignation.value = null;
    }
  } catch (error) {
    console.error('사직서 조회 오류:', error);
    myResignation.value = null;
  }
};

// 사직서 상태 텍스트
const getResignationStatusText = (status: string) => {
  const statusMap: Record<string, string> = {
    'submitted': '제출됨',
    'approved': '승인됨',
    'rejected': '반려됨',
    'pending': '대기중'
  };
  return statusMap[status] || status;
};

// 사직서 상태 클래스
const getResignationStatusClass = (status: string) => {
  return status || 'submitted';
};

// 카테고리 열기
const openCategory = (category: string) => {
  switch (category) {
    case 'contract':
      showContractDialog.value = true;
      fetchContracts();
      break;
    case 'payslip':
      router.push('/emp/payslip');
      break;
    case 'record':
      showNotice('서비스 기록지 기능은 준비 중입니다.');
      break;
    case 'certificate':
      showCertificateDialog.value = true;
      selectedCertType.value = '';
      certPurpose.value = '';
      break;
  }
};

// 근로계약서 조회
const fetchContracts = async () => {
  try {
    contracts.value = [];
  } catch (error) {
    console.error('근로계약서 조회 오류:', error);
  }
};

// 증명서 발급 요청
const requestCertificate = async () => {
  if (!selectedCertType.value) return;
  
  isRequesting.value = true;
  try {
    await new Promise(resolve => setTimeout(resolve, 1000));
    showCertificateDialog.value = false;
    showSuccess('증명서 발급 요청이 완료되었습니다.');
  } catch (error) {
    console.error('증명서 요청 오류:', error);
    showError('증명서 발급 요청에 실패했습니다.');
  } finally {
    isRequesting.value = false;
  }
};

// 사직서 제출
const submitResignation = async () => {
  if (!canSubmitResignation.value) return;
  
  isSubmitting.value = true;
  try {
    const signatureDataUrl = signatureCanvas.value?.toDataURL('image/png') || '';
    
    const response = await axios.post('/api/emp/resignation', {
      emp_idx: empIdx.value,
      emp_code: empCode.value,
      m_sisul_code: sisulCode.value,
      sisul_name: sisulName.value,
      emp_name: empName.value,
      emp_job: empJob.value,
      resign_reason: resignationData.value.reason,
      resign_date: resignationData.value.resignDate,
      submit_date: todayISO,
      signature_data: signatureDataUrl
    }, { withCredentials: true });
    
    if (response.data.success) {
      closeResignationDialog();
      await fetchMyResignation();
      showSuccess('사직서가 제출되었습니다.');
    } else {
      showError(response.data.message || '사직서 제출에 실패했습니다.');
    }
  } catch (error: any) {
    console.error('사직서 제출 오류:', error);
    showError(error.response?.data?.message || '사직서 제출에 실패했습니다.');
  } finally {
    isSubmitting.value = false;
  }
};

// PDF 다운로드
const downloadResignationPDF = () => {
  if (!myResignation.value?.pdf_path) return;
  
  const pdfUrl = `/api/emp${myResignation.value.pdf_path}`;
  window.open(pdfUrl, '_blank');
};

// 사직서 삭제 확인
const confirmDeleteResignation = () => {
  showDeleteConfirmDialog.value = true;
};

// 사직서 삭제
const deleteResignation = async () => {
  if (!myResignation.value) return;
  
  isDeleting.value = true;
  try {
    const response = await axios.delete(
      `/api/emp/resignation/${empIdx.value}/${myResignation.value.id}`,
      { withCredentials: true }
    );
    
    if (response.data.success) {
      showDeleteConfirmDialog.value = false;
      showResignationDetailDialog.value = false;
      myResignation.value = null;
      showSuccess('사직서가 삭제되었습니다.');
    } else {
      showError(response.data.message || '사직서 삭제에 실패했습니다.');
    }
  } catch (error: any) {
    console.error('사직서 삭제 오류:', error);
    showError(error.response?.data?.message || '사직서 삭제에 실패했습니다.');
  } finally {
    isDeleting.value = false;
  }
};

// 최근 문서 조회
const fetchRecentDocuments = async () => {
  try {
    const response = await axios.get(`/api/emp/documents/${empIdx.value}`, {
      withCredentials: true
    });
    
    if (response.data.success) {
      recentDocuments.value = response.data.data || [];
    }
  } catch (error) {
    console.error('문서 조회 오류:', error);
  }
};

// 문서 아이콘 가져오기
const getDocIcon = (type: string) => {
  const icons: Record<string, string> = {
    contract: 'mdi-file-sign',
    payslip: 'mdi-currency-krw',
    record: 'mdi-clipboard-text',
    certificate: 'mdi-certificate',
    resignation: 'mdi-file-remove'
  };
  return icons[type] || 'mdi-file-document';
};

// 날짜 포맷
const formatDate = (dateStr: string) => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  return `${date.getFullYear()}.${String(date.getMonth() + 1).padStart(2, '0')}.${String(date.getDate()).padStart(2, '0')}`;
};

// 문서 열기
const openDocument = (doc: any) => {
};

// 문서 다운로드
const downloadDocument = (doc: any) => {
  showNotice('다운로드 기능은 준비 중입니다.');
};

// 계약서 보기
const viewContract = (contract: any) => {
  showNotice('계약서 보기 기능은 준비 중입니다.');
};

// 계약서 다운로드
const downloadContract = (contract: any) => {
  showNotice('다운로드 기능은 준비 중입니다.');
};

// 알림 표시
const showSuccess = (message: string) => {
  snackbarMessage.value = message;
  snackbarColor.value = 'success';
  showSnackbar.value = true;
};

const showError = (message: string) => {
  snackbarMessage.value = message;
  snackbarColor.value = 'error';
  showSnackbar.value = true;
};

const showNotice = (message: string) => {
  snackbarMessage.value = message;
  snackbarColor.value = 'info';
  showSnackbar.value = true;
};

// 초기 데이터 로드
onMounted(async () => {
  isLoading.value = true;
  try {
    await fetchMyResignation();
  } catch (error) {
    console.error('문서 조회 오류:', error);
  } finally {
    isLoading.value = false;
  }
});
</script>

<style scoped lang="scss">
.emp-documents-page {
  min-height: 100vh;
  background: #f5f5f5;
}

.app-header {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 14px 16px;
  background: linear-gradient(135deg, #059669 0%, #10b981 100%);
  color: white;
  position: sticky;
  top: 0;
  z-index: 100;
  
  .header-title {
    font-size: 17px;
    font-weight: 600;
  }
}

.page-content {
  padding: 16px;
  padding-bottom: 80px;
}

.document-categories {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 24px;
}

.category-card {
  display: flex;
  align-items: center;
  gap: 12px;
  background: white;
  border-radius: 12px;
  padding: 16px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }
  
  &.resignation {
    border: 1px solid #fecaca;
    background: linear-gradient(135deg, #fff5f5 0%, #fff 100%);
  }
}

.card-icon {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  
  &.contract { background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%); }
  &.payslip { background: linear-gradient(135deg, #10b981 0%, #059669 100%); }
  &.record { background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%); }
  &.certificate { background: linear-gradient(135deg, #8b5cf6 0%, #6d28d9 100%); }
  &.resignation { background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%); }
}

.card-content {
  flex: 1;
  
  h3 {
    font-size: 16px;
    font-weight: 600;
    color: #111827;
    margin: 0 0 2px 0;
  }
  
  p {
    font-size: 13px;
    color: #6b7280;
    margin: 0;
  }
}

.card-badge {
  background: #059669;
  color: white;
  font-size: 12px;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 10px;
}

.card-status span {
  font-size: 12px;
  font-weight: 600;
  padding: 4px 10px;
  border-radius: 10px;
  
  &.submitted {
    background: #fef3c7;
    color: #d97706;
  }
  
  &.approved {
    background: #d1fae5;
    color: #059669;
  }
  
  &.rejected {
    background: #fee2e2;
    color: #dc2626;
  }
}

.section-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 16px;
  font-weight: 600;
  color: #374151;
  margin-bottom: 12px;
}

.document-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.document-item {
  display: flex;
  align-items: center;
  gap: 12px;
  background: white;
  border-radius: 10px;
  padding: 12px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  cursor: pointer;
  
  .doc-icon {
    width: 36px;
    height: 36px;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    
    &.contract { background: #dbeafe; }
    &.payslip { background: #d1fae5; }
    &.record { background: #fef3c7; }
    &.certificate { background: #ede9fe; }
    &.resignation { background: #fee2e2; }
  }
  
  .doc-info {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 2px;
    
    .doc-name {
      font-size: 14px;
      font-weight: 500;
      color: #111827;
    }
    
    .doc-date {
      font-size: 12px;
      color: #9ca3af;
    }
  }
  
  .download-btn {
    width: 32px;
    height: 32px;
    border-radius: 6px;
    border: none;
    background: #f3f4f6;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    
    &:hover {
      background: #e5e7eb;
    }
  }
}

.empty-notice {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  color: #9ca3af;
  
  p {
    font-size: 15px;
    margin: 12px 0 4px;
  }
  
  span {
    font-size: 13px;
  }
}

// 다이얼로그 공통 스타일
.document-dialog-card {
  border-radius: 16px !important;
  overflow: hidden;
  
  .dialog-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 16px 20px;
    background: #f9fafb;
    border-bottom: 1px solid #e5e7eb;
    
    h3 {
      font-size: 18px;
      font-weight: 600;
      margin: 0;
    }
    
    .close-btn {
      width: 32px;
      height: 32px;
      border-radius: 50%;
      border: none;
      background: transparent;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      
      &:hover {
        background: #e5e7eb;
      }
    }
  }
  
  .dialog-content {
    padding: 20px;
  }
}

.no-data {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 32px;
  color: #9ca3af;
  
  p {
    margin-top: 12px;
    font-size: 14px;
  }
}

// 증명서 발급
.certificate-options {
  display: flex;
  gap: 12px;
  margin-bottom: 20px;
  
  .cert-option {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
    padding: 16px;
    border: 2px solid #e5e7eb;
    border-radius: 12px;
    background: white;
    cursor: pointer;
    transition: all 0.2s ease;
    
    span {
      font-size: 13px;
      color: #6b7280;
    }
    
    &.active {
      border-color: #059669;
      background: #ecfdf5;
      
      span {
        color: #059669;
        font-weight: 600;
      }
    }
  }
}

.cert-purpose {
  margin-bottom: 20px;
  
  label {
    display: block;
    font-size: 13px;
    font-weight: 500;
    color: #374151;
    margin-bottom: 6px;
  }
  
  input {
    width: 100%;
    padding: 12px;
    border: 1px solid #d1d5db;
    border-radius: 8px;
    font-size: 14px;
    
    &:focus {
      outline: none;
      border-color: #059669;
    }
  }
}

.request-btn {
  width: 100%;
  padding: 14px;
  background: linear-gradient(135deg, #059669 0%, #047857 100%);
  color: white;
  border: none;
  border-radius: 10px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:disabled {
    background: #d1d5db;
    cursor: not-allowed;
  }
}

.cert-notice {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 12px;
  font-size: 12px;
  color: #d97706;
}

// 사직서 작성 다이얼로그 (간소화)
.resignation-write-card {
  border-radius: 20px !important;
  overflow: hidden;
  
  .write-header {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 20px 24px;
    background: #fef2f2;
    border-bottom: 1px solid #fecaca;
    
    h2 {
      flex: 1;
      font-size: 18px;
      font-weight: 700;
      color: #991b1b;
      margin: 0;
    }
    
    .close-btn {
      width: 36px;
      height: 36px;
      border-radius: 50%;
      border: none;
      background: transparent;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      color: #6b7280;
      
      &:hover {
        background: #fee2e2;
        color: #dc2626;
      }
    }
  }
  
  .write-content {
    padding: 24px;
    
    .input-group {
      margin-bottom: 20px;
      
      label {
        display: flex;
        align-items: center;
        gap: 6px;
        font-size: 14px;
        font-weight: 600;
        color: #374151;
        margin-bottom: 10px;
      }
      
      .date-input {
        width: 100%;
        padding: 14px 16px;
        border: 2px solid #e5e7eb;
        border-radius: 12px;
        font-size: 15px;
        color: #111827;
        background: white;
        
        &:focus {
          outline: none;
          border-color: #dc2626;
        }
      }
      
      .input-hint {
        margin: 8px 0 0;
        font-size: 12px;
        color: #9ca3af;
      }
      
      .reason-input {
        width: 100%;
        padding: 14px 16px;
        border: 2px solid #e5e7eb;
        border-radius: 12px;
        font-size: 14px;
        color: #111827;
        resize: none;
        font-family: inherit;
        
        &:focus {
          outline: none;
          border-color: #dc2626;
        }
        
        &::placeholder {
          color: #9ca3af;
        }
      }
      
      .signature-box {
        position: relative;
        background: #f9fafb;
        border: 2px dashed #d1d5db;
        border-radius: 12px;
        padding: 8px;
        
        .signature-canvas {
          width: 100%;
          height: 100px;
          background: white;
          border-radius: 8px;
          cursor: crosshair;
          touch-action: none;
        }
        
        .clear-btn {
          position: absolute;
          bottom: 14px;
          right: 14px;
          display: flex;
          align-items: center;
          gap: 4px;
          padding: 6px 12px;
          background: white;
          border: 1px solid #d1d5db;
          border-radius: 6px;
          font-size: 12px;
          color: #6b7280;
          cursor: pointer;
          
          &:hover {
            background: #f3f4f6;
            color: #dc2626;
            border-color: #dc2626;
          }
        }
      }
    }
    
    .info-notice {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 12px 14px;
      background: #e0f2fe;
      border-radius: 10px;
      margin-bottom: 12px;
      
      span {
        font-size: 13px;
        color: #0369a1;
      }
    }
    
    .warning-notice {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 12px 14px;
      background: #fef2f2;
      border-radius: 10px;
      margin-bottom: 20px;
      
      span {
        font-size: 13px;
        color: #dc2626;
      }
    }
    
    .submit-btn {
      width: 100%;
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 8px;
      padding: 16px;
      background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%);
      color: white;
      border: none;
      border-radius: 12px;
      font-size: 16px;
      font-weight: 600;
      cursor: pointer;
      
      &:disabled {
        background: #d1d5db;
        cursor: not-allowed;
      }
      
      &:not(:disabled):hover {
        opacity: 0.9;
      }
    }
  }
}

// 기존 사직서 양식 스타일 (사용하지 않음)
.resignation-letter-card-old {
  border-radius: 16px !important;
  overflow: hidden;
}

.resignation-letter-header-old {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%);
  
  h2 {
    font-size: 22px;
    font-weight: 700;
    color: white;
    margin: 0;
    letter-spacing: 8px;
  }
  
  .close-btn {
    width: 36px;
    height: 36px;
    border-radius: 50%;
    border: none;
    background: rgba(255, 255, 255, 0.2);
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    
    &:hover {
      background: rgba(255, 255, 255, 0.3);
    }
  }
}

.resignation-letter-content {
  padding: 24px;
  
  .letter-form {
    margin-bottom: 24px;
    
    .form-row {
      display: flex;
      gap: 8px;
      margin-bottom: 16px;
      align-items: flex-start;
      
      .form-label {
        flex-shrink: 0;
        width: 80px;
        font-size: 14px;
        font-weight: 500;
        color: #374151;
        padding-top: 10px;
      }
      
      .form-value {
        font-size: 14px;
        color: #111827;
        padding-top: 10px;
      }
      
      .form-input-wrapper {
        flex: 1;
      }
      
      .reason-textarea {
        width: 100%;
        padding: 10px 12px;
        border: 1px solid #d1d5db;
        border-radius: 8px;
        font-size: 14px;
        resize: none;
        
        &:focus {
          outline: none;
          border-color: #dc2626;
        }
      }
    }
  }
  
  .letter-body {
    text-align: center;
    margin-bottom: 24px;
    padding: 20px;
    background: #f9fafb;
    border-radius: 12px;
    
    p {
      font-size: 14px;
      color: #374151;
      line-height: 2;
      margin: 0;
    }
    
    .resign-date-row {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 8px;
      margin: 12px 0;
      
      .resign-date-input {
        padding: 10px 14px;
        border: 2px solid #dc2626;
        border-radius: 8px;
        font-size: 15px;
        font-weight: 600;
        color: #dc2626;
        text-align: center;
        background: white;
        
        &:focus {
          outline: none;
        }
      }
      
      .date-suffix {
        font-size: 14px;
        font-weight: 600;
        color: #111827;
      }
    }
  }
  
  .letter-date {
    text-align: center;
    font-size: 14px;
    color: #6b7280;
    margin-bottom: 24px;
  }
  
  .signature-section {
    margin-bottom: 20px;
    
    .signature-row {
      display: flex;
      align-items: center;
      gap: 12px;
      justify-content: flex-end;
      
      .signature-label {
        font-size: 14px;
        font-weight: 500;
        color: #374151;
      }
      
      .signature-area {
        position: relative;
        
        .signature-canvas {
          width: 160px;
          height: 60px;
          border: 2px solid #e5e7eb;
          border-radius: 8px;
          background: #fafafa;
          cursor: crosshair;
          touch-action: none;
        }
        
        .clear-signature-btn {
          position: absolute;
          bottom: 4px;
          right: 4px;
          display: flex;
          align-items: center;
          gap: 4px;
          padding: 4px 8px;
          background: rgba(255, 255, 255, 0.9);
          border: 1px solid #e5e7eb;
          border-radius: 4px;
          font-size: 11px;
          color: #6b7280;
          cursor: pointer;
          
          &:hover {
            background: white;
            color: #dc2626;
          }
        }
      }
      
      .seal-text {
        font-size: 14px;
        font-weight: 500;
        color: #374151;
      }
    }
    
    .signature-hint {
      text-align: center;
      font-size: 12px;
      color: #9ca3af;
      margin-top: 8px;
    }
  }
  
  .resignation-warning {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    padding: 12px;
    background: #fef2f2;
    border-radius: 8px;
    margin-bottom: 16px;
    
    span {
      font-size: 13px;
      color: #dc2626;
    }
  }
  
  .submit-btn.resignation {
    width: 100%;
    padding: 14px;
    background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%);
    color: white;
    border: none;
    border-radius: 10px;
    font-size: 15px;
    font-weight: 600;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    
    &:disabled {
      background: #d1d5db;
      cursor: not-allowed;
    }
  }
}

// 사직서 상세 다이얼로그
.resignation-detail-card {
  border-radius: 16px !important;
  overflow: hidden;
}

.resignation-detail-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 18px 24px;
  background: linear-gradient(135deg, #374151 0%, #1f2937 100%);
  
  h2 {
    font-size: 18px;
    font-weight: 600;
    color: white;
    margin: 0;
  }
  
  .close-btn {
    width: 32px;
    height: 32px;
    border-radius: 50%;
    border: none;
    background: rgba(255, 255, 255, 0.2);
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    
    &:hover {
      background: rgba(255, 255, 255, 0.3);
    }
  }
}

.resignation-detail-content {
  padding: 24px;
  
  .status-badge {
    display: inline-block;
    padding: 6px 14px;
    border-radius: 20px;
    font-size: 13px;
    font-weight: 600;
    margin-bottom: 20px;
    
    &.submitted {
      background: #fef3c7;
      color: #d97706;
    }
    
    &.approved {
      background: #d1fae5;
      color: #059669;
    }
    
    &.rejected {
      background: #fee2e2;
      color: #dc2626;
    }
  }
  
  .detail-info {
    margin-bottom: 24px;
    
    .info-row {
      display: flex;
      padding: 12px 0;
      border-bottom: 1px solid #f3f4f6;
      
      &:last-child {
        border-bottom: none;
      }
      
      .info-label {
        width: 90px;
        flex-shrink: 0;
        font-size: 13px;
        font-weight: 500;
        color: #6b7280;
      }
      
      .info-value {
        flex: 1;
        font-size: 14px;
        color: #111827;
        
        &.reason {
          line-height: 1.5;
        }
      }
    }
  }
  
  .download-pdf-btn {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 14px;
    background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
    color: white;
    border: none;
    border-radius: 10px;
    font-size: 15px;
    font-weight: 600;
    cursor: pointer;
    margin-bottom: 12px;
    
    &:hover {
      opacity: 0.9;
    }
  }
  
  .delete-btn {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 14px;
    background: white;
    color: #dc2626;
    border: 2px solid #dc2626;
    border-radius: 10px;
    font-size: 15px;
    font-weight: 600;
    cursor: pointer;
    
    &:hover {
      background: #fef2f2;
    }
  }
  
  .delete-notice {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    margin-top: 16px;
    font-size: 12px;
    color: #9ca3af;
  }
}

// 삭제 확인 다이얼로그
.confirm-dialog-card {
  border-radius: 16px !important;
  overflow: hidden;
  
  .confirm-content {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 32px 24px 20px;
    text-align: center;
    
    h3 {
      font-size: 18px;
      font-weight: 600;
      color: #111827;
      margin: 16px 0 8px;
    }
    
    p {
      font-size: 14px;
      color: #6b7280;
      line-height: 1.5;
      margin: 0;
    }
  }
  
  .confirm-actions {
    display: flex;
    gap: 12px;
    padding: 16px 24px 24px;
    
    button {
      flex: 1;
      padding: 12px;
      border-radius: 10px;
      font-size: 15px;
      font-weight: 600;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
    }
    
    .cancel-btn {
      background: #f3f4f6;
      color: #374151;
      border: none;
      
      &:hover {
        background: #e5e7eb;
      }
    }
    
    .delete-confirm-btn {
      background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%);
      color: white;
      border: none;
      
      &:disabled {
        background: #d1d5db;
        cursor: not-allowed;
      }
    }
  }
}

.contract-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 0;
  border-bottom: 1px solid #f3f4f6;
  
  &:last-child {
    border-bottom: none;
  }
  
  .contract-info {
    display: flex;
    flex-direction: column;
    gap: 4px;
    
    .contract-period {
      font-size: 14px;
      font-weight: 500;
      color: #111827;
    }
    
    .contract-type {
      font-size: 12px;
      color: #6b7280;
    }
  }
  
  .contract-actions {
    display: flex;
    gap: 8px;
    
    button {
      width: 32px;
      height: 32px;
      border-radius: 6px;
      border: none;
      background: #f3f4f6;
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      
      &:hover {
        background: #e5e7eb;
      }
    }
  }
}
</style>
